import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import FeatherIcon from "feather-icons-react";

export default function Sidebar() {
  const location = useLocation();
  const currentPath = location.pathname;

  const [openMenu, setOpenMenu] = useState<string | null>(null);

  const toggleMenu = (menu: string) => {
    setOpenMenu((prev) => (prev === menu ? null : menu));
  };

  // Auto open menu when route matches any of its children
  useEffect(() => {
    if (
      [
        "/business-info",
        "/business-info-edit",
        "/services",
        "/edit-services",
        "/configurations",
        "/edit-configurations",
        // "/packages",
        "/products",
        "/items",
        "/categorization",
        "/edit-categorization",
      ].some((path) => currentPath.startsWith(path))
    ) {
      setOpenMenu("business");
    } else if (
      ["/staffs", "/staff-services", "/staff-payments"].some((path) =>
        currentPath.startsWith(path)
      )
    ) {
      setOpenMenu("staff");
    } else if (
      ["/customers", "/customer-queries"].some((path) =>
        currentPath.startsWith(path)
      )
    ) {
      setOpenMenu("customer");
    } else {
      setOpenMenu(null);
    }
  }, [currentPath]);

  return (
    <nav id="sidebar" className="sidebar">
      <div className="sidebar-content">
        <ul className="sidebar-nav">
          {/* --- Single Main Menus --- */}
          <li className={`sidebar-item ${currentPath === "/dashboard" ? "active" : ""}`}>
            <Link className="sidebar-link" to="/dashboard">
              <FeatherIcon icon="sliders" />{" "}
              <span className="align-middle">Dashboard</span>
            </Link>
          </li>

          <li className={`sidebar-item ${currentPath.startsWith("/orders") ? "active" : ""}`}>
            <Link className="sidebar-link" to="/orders">
              <FeatherIcon icon="shopping-cart" />{" "}
              <span className="align-middle">Orders</span>
            </Link>
          </li>

          <li className={`sidebar-item ${currentPath.startsWith("/bookings") ? "active" : ""}`}>
            <Link className="sidebar-link" to="/bookings">
              <FeatherIcon icon="calendar" />{" "}
              <span className="align-middle">Bookings</span>
            </Link>
          </li>

          <li className={`sidebar-item ${currentPath.startsWith("/payments") ? "active" : ""}`}>
            <Link className="sidebar-link" to="/payments">
              <FeatherIcon icon="credit-card" />{" "}
              <span className="align-middle">Payments</span>
            </Link>
          </li>

          {/* --- Collapsible: Staff Management --- */}
          <li
            className={`sidebar-item ${
              openMenu === "staff" ? "active" : ""
            }`}
          >
            <div
              className="sidebar-link d-flex justify-content-between align-items-center pointer"
              onClick={() => toggleMenu("staff")}
            >
              <div>
                <FeatherIcon icon="users" />{" "}
                <span className="align-middle">Staffs</span>
              </div>
              <FeatherIcon
                icon={openMenu === "staff" ? "chevron-up" : "chevron-down"}
                size={16}
              />
            </div>

            <ul
              className={`collapse list-unstyled ps-4 ${
                openMenu === "staff" ? "show" : ""
              }`}
            >
              {[
                { to: "/staffs", label: "Staff List", icon: "user" },
                { to: "/staff-services", label: "Staff Services", icon: "briefcase" },
                { to: "/staff-payments", label: "Staff Payments", icon: "dollar-sign" },
              ].map((link) => (
                <li
                  key={link.to}
                  className={`sidebar-item ${
                    currentPath.startsWith(link.to) ? "active" : ""
                  }`}
                >
                  <Link className="sidebar-link" to={link.to}>
                    <FeatherIcon icon={link.icon as any} /> {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </li>

          {/* --- Collapsible: Customer Management --- */}
          <li
            className={`sidebar-item ${
              openMenu === "customer" ? "active" : ""
            }`}
          >
            <div
              className="sidebar-link d-flex justify-content-between align-items-center pointer"
              onClick={() => toggleMenu("customer")}
            >
              <div>
                <FeatherIcon icon="user" />{" "}
                <span className="align-middle">Customers</span>
              </div>
              <FeatherIcon
                icon={openMenu === "customer" ? "chevron-up" : "chevron-down"}
                size={16}
              />
            </div>

            <ul
              className={`collapse list-unstyled ps-4 ${
                openMenu === "customer" ? "show" : ""
              }`}
            >
              {[
                { to: "/customers", label: "Customer List", icon: "users" },
                { to: "/customer-queries", label: "Queries", icon: "message-circle" },
              ].map((link) => (
                <li
                  key={link.to}
                  className={`sidebar-item ${
                    currentPath.startsWith(link.to) ? "active" : ""
                  }`}
                >
                  <Link className="sidebar-link" to={link.to}>
                    <FeatherIcon icon={link.icon as any} /> {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </li>

          {/* --- Collapsible: Business Settings --- */}
          <li
            className={`sidebar-item ${
              openMenu === "business" ? "active" : ""
            }`}
          >
            <div
              className="sidebar-link d-flex justify-content-between align-items-center pointer"
              onClick={() => toggleMenu("business")}
            >
              <div>
                <FeatherIcon icon="settings" />{" "}
                <span className="align-middle">Settings</span>
              </div>
              <FeatherIcon
                icon={openMenu === "business" ? "chevron-up" : "chevron-down"}
                size={16}
              />
            </div>

            <ul
              className={`collapse list-unstyled ps-4 ${
                openMenu === "business" ? "show" : ""
              }`}
            >
              {[
                { to: "/business-info", label: "Info", icon: "info" },
                { to: "/categorization", label: "Categorization", icon: "table" },
                { to: "/services", label: "Services", icon: "list" },
                { to: "/configurations", label: "Configurations", icon: "box" },
                // { to: "/packages", label: "Packages", icon: "package" },
                // { to: "/items", label: "Items", icon: "list" },
              ].map((link) => (
                <li
                  key={link.to}
                  className={`sidebar-item ${
                    currentPath.startsWith(link.to) ? "active" : ""
                  }`}
                >
                  <Link className="sidebar-link" to={link.to}>
                    <FeatherIcon icon={link.icon as any} /> {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </li>
          
        </ul>
      </div>
    </nav>
  );
}
